import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, CalendarCheck, BellRing, Plane, Globe2, Sparkles, Star, Quote } from "lucide-react";
import { SERVICES, SERVICES_BY_SLUG, BRAND } from "@/lib/data";
import { createCheckout, api } from "@/lib/api";
import Newsletter from "@/components/Newsletter";
import JsonLd from "@/components/JsonLd";
import useSeo from "@/hooks/useSeo";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = SERVICES_BY_SLUG[slug];
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState(null);

  useEffect(() => {
    if (!slug) return;
    api
      .get("/reviews", { params: { service_slug: slug } })
      .then((r) => {
        setReviews(r.data.reviews || []);
        setAggregate(r.data.aggregate || null);
      })
      .catch(() => {});
  }, [slug]);

  useSeo({
    title: service ? service.name : "Service",
    description: service ? service.tagline : undefined,
    path: service ? `/services/${service.slug}` : "/services",
    image: service ? service.image : undefined,
  });

  if (!service) return <Navigate to="/services" replace />;

  const accent =
    service.color === "gold" ? "bg-gold" : service.color === "teal" ? "bg-teal" : "bg-navy";
  const isCreditRepair = service.slug === "credit-repair";
  const isTaxes = service.slug === "taxes";
  const directEmail =
    isCreditRepair ? BRAND.emails.credit :
    isTaxes ? BRAND.emails.tax :
    null;

  async function handleBuy(tier) {
    setLoadingTier(tier.id);
    setError("");
    try {
      const { url } = await createCheckout({
        service_id: tier.id,
        service_name: `${service.name} — ${tier.name}`,
        amount_cents: tier.price_cents,
        quantity: 1,
      });
      if (url) window.location.href = url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to start checkout right now. Please try again or contact us.");
      setLoadingTier(null);
    }
  }

  const others = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);
  const isTravel = service.slug === "travel";

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.description || service.tagline,
    provider: {
      "@type": "Organization",
      name: "ConnectHer HQ",
      url: "https://thechhq.com",
    },
    serviceType: service.name,
    areaServed: "US",
    url: `https://thechhq.com/services/${service.slug}`,
    image: service.image,
    ...(service.tiers && service.tiers.length > 0
      ? {
          offers: service.tiers
            .filter((t) => t.price_cents > 0)
            .map((t) => ({
              "@type": "Offer",
              name: t.name,
              price: (t.price_cents / 100).toFixed(2),
              priceCurrency: "USD",
            })),
        }
      : {}),
    ...(aggregate && aggregate.count > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: aggregate.average,
            reviewCount: aggregate.count,
            bestRating: 5,
            worstRating: 1,
          },
          review: reviews.slice(0, 5).map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.author },
            reviewRating: {
              "@type": "Rating",
              ratingValue: r.rating,
              bestRating: 5,
            },
            reviewBody: r.body,
          })),
        }
      : {}),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://thechhq.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: "https://thechhq.com/services",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: service.name,
        item: `https://thechhq.com/services/${service.slug}`,
      },
    ],
  };

  return (
    <>
      <JsonLd id={`jsonld-service-${service.slug}`} data={serviceSchema} />
      <JsonLd id={`jsonld-breadcrumb-${service.slug}`} data={breadcrumbSchema} />
      <section className="pt-12 md:pt-20 pb-12" data-testid={`service-hero-${service.slug}`}>
        <div className="container-app grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <Link to="/services" className="text-sm text-navy/60 hover:text-navy inline-flex items-center gap-1 mb-6">
              ← All services
            </Link>
            <div className="eyebrow">
              {service.comingSoon ? "Coming Soon" : "ConnectHer Service"}
            </div>
            <h1 className="mt-3 text-5xl md:text-6xl leading-tight">{service.name}</h1>
            <p className="mt-5 text-xl text-navy/70 leading-relaxed">{service.tagline}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              {service.comingSoon ? (
                <Link to="/contact" data-testid="service-waitlist-cta" className="btn-primary">
                  <BellRing className="w-4 h-4" /> Join the Waitlist
                </Link>
              ) : isCreditRepair ? (
                <a
                  href={BRAND.cdmSignupUrl}
                  target="_blank" rel="noreferrer"
                  data-testid="cdm-signup-cta"
                  className="btn-primary"
                >
                  Start Credit Repair <ArrowUpRight className="w-4 h-4" />
                </a>
              ) : (
                <Link to="/book" data-testid="service-book-cta" className="btn-primary">
                  <CalendarCheck className="w-4 h-4" /> Book a Consultation
                </Link>
              )}
              <Link to="/intake" data-testid="service-intake-secondary" className="btn-secondary">
                Start Intake
              </Link>
            </div>
            {directEmail && (
              <p className="mt-4 text-sm text-navy/60" data-testid="service-direct-email">
                Questions? Email{" "}
                <a href={`mailto:${directEmail}`} className="text-gold hover:underline">{directEmail}</a>
              </p>
            )}
            {error && <p data-testid="service-buy-error" className="text-sm text-red-500 mt-3">{error}</p>}
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="md:col-span-6">
            <div className="relative">
              <img src={service.image} alt={service.name} className="rounded-[2rem] w-full h-[480px] object-cover shadow-cardHover" />
              <div className={`absolute -bottom-5 -left-5 ${accent} text-white rounded-2xl px-5 py-4 shadow-card`}>
                <div className="text-[10px] uppercase tracking-widest opacity-80">{service.comingSoon ? "Launching" : "Pricing"}</div>
                <div className="font-serif text-2xl">{service.price_label}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section bg-white" data-testid="service-details">
        <div className="container-app grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="eyebrow">{service.comingSoon ? "What to expect" : "What's included"}</div>
            <h2 className="mt-3 text-4xl">A closer look.</h2>
            <p className="mt-5 text-navy/70 leading-relaxed">{service.description}</p>
            <ul className="mt-8 space-y-4">
              {service.bullets.map((b, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3"
                >
                  <span className="mt-1 w-6 h-6 rounded-full bg-gold/15 text-gold flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4" />
                  </span>
                  <span className="text-navy/85 leading-relaxed">{b}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          <aside className="md:col-span-5">
            <div className="card p-7 sticky top-28">
              {service.comingSoon ? (
                <>
                  <h4 className="text-2xl mb-4">Be first in line</h4>
                  <p className="text-sm text-navy/70 mb-5">
                    Join the early-access list and we'll let you know the moment enrollment opens.
                  </p>
                  <Link to="/contact" className="btn-primary w-full justify-center" data-testid="service-waitlist-secondary">
                    Join Waitlist
                  </Link>
                </>
              ) : service.tiers && service.tiers.length > 0 ? (
                <>
                  <h4 className="text-2xl mb-4">Choose your option</h4>
                  <ul className="space-y-3">
                    {service.tiers.map((t) => (
                      <li key={t.id} className="flex items-center justify-between gap-3 border-b border-navy/5 last:border-0 pb-3 last:pb-0" data-testid={`tier-${t.id}`}>
                        <div>
                          <div className="text-navy font-medium">{t.name}</div>
                          <div className="text-xs text-navy/55">{t.label}</div>
                        </div>
                        <button
                          onClick={() => handleBuy(t)}
                          disabled={loadingTier === t.id}
                          data-testid={`tier-buy-${t.id}`}
                          className="text-xs bg-gold text-white rounded-full px-4 py-2 hover:bg-gold-dark transition disabled:opacity-60"
                        >
                          {loadingTier === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Buy"}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-6 border-t border-navy/5 flex flex-col gap-3">
                    <Link to="/book" className="btn-teal text-center">Book Consultation</Link>
                    <Link to="/contact" className="btn-secondary text-center">Ask a Question</Link>
                  </div>
                </>
              ) : (
                <>
                  <h4 className="text-2xl mb-4">How it works</h4>
                  <ol className="space-y-5 text-sm text-navy/75">
                    <li className="flex gap-3"><span className="w-7 h-7 rounded-full bg-navy text-soft flex items-center justify-center text-xs flex-shrink-0">1</span><div><strong className="text-navy">Discovery call.</strong> A free 15-minute call to confirm fit.</div></li>
                    <li className="flex gap-3"><span className="w-7 h-7 rounded-full bg-navy text-soft flex items-center justify-center text-xs flex-shrink-0">2</span><div><strong className="text-navy">Intake.</strong> Tell us the details we need to quote.</div></li>
                    <li className="flex gap-3"><span className="w-7 h-7 rounded-full bg-navy text-soft flex items-center justify-center text-xs flex-shrink-0">3</span><div><strong className="text-navy">Service delivery.</strong> We'll handle the work and keep you updated.</div></li>
                  </ol>
                  <div className="mt-6 pt-6 border-t border-navy/5 flex flex-col gap-3">
                    <Link to="/book" className="btn-teal text-center">Book Consultation</Link>
                    <Link to="/contact" className="btn-secondary text-center">Request a Quote</Link>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      </section>

      {reviews && reviews.length > 0 && (
        <section className="section bg-white" data-testid={`service-testimonials-${service.slug}`}>
          <div className="container-app">
            <div className="max-w-2xl mb-10">
              <div className="eyebrow flex items-center gap-2">
                <Star className="w-3.5 h-3.5 fill-gold text-gold" /> What clients say
              </div>
              <h2 className="mt-3 text-4xl md:text-5xl">
                {aggregate?.average ? `${aggregate.average} of 5 stars` : "Client reviews"}{" "}
                <span className="text-navy/50 text-2xl md:text-3xl font-sans not-italic">
                  · {aggregate?.count || reviews.length} review{(aggregate?.count || reviews.length) === 1 ? "" : "s"}
                </span>
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.slice(0, 6).map((r, i) => (
                <div key={r.review_id || i} className="card p-6">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <Star
                        key={idx}
                        className={`w-4 h-4 ${
                          idx < (r.rating || 5) ? "text-gold fill-current" : "text-navy/15"
                        }`}
                      />
                    ))}
                  </div>
                  <Quote className="w-5 h-5 text-gold/40 mb-2" />
                  <p className="text-navy/80 italic leading-relaxed text-sm">
                    &ldquo;{r.body}&rdquo;
                  </p>
                  <div className="mt-5 pt-4 border-t border-navy/5">
                    <div className="font-serif text-base text-navy">{r.author}</div>
                    {r.location && <div className="text-xs text-navy/55">{r.location}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section" data-testid="service-related">
        <div className="container-app">
          {isTravel && (
            <div className="mb-16 grid md:grid-cols-2 gap-6" data-testid="travel-perks">
              <div className="card p-7 bg-gold/5 border-gold/30">
                <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">For ConnectHer Clients</div>
                <h3 className="text-2xl text-navy">Travel perks built into every ConnectHer relationship.</h3>
                <p className="mt-3 text-sm text-navy/70 leading-relaxed">
                  If you&apos;re already a bookkeeping, tax, consulting, credit repair, or business client &mdash; travel comes with extra benefits.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-navy/80">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> Complimentary travel consultations</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> Exclusive travel promotions reserved for clients</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> First access to ConnectHer group trip opportunities</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> Future client appreciation trips</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> Referral travel credits when you refer new clients</li>
                </ul>
              </div>
              <div className="card p-7 bg-navy text-soft border-navy">
                <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">When fees apply</div>
                <h3 className="text-2xl text-soft">Most planning is complimentary.</h3>
                <p className="mt-3 text-sm text-soft/80 leading-relaxed">
                  We earn through commissions from our luxury partners, so most travel costs you nothing extra. Research fees apply only when:
                </p>
                <ul className="mt-5 space-y-2 text-sm text-soft/85">
                  <li className="flex gap-2"><span className="text-gold mt-0.5">•</span> The trip requires significant custom research</li>
                  <li className="flex gap-2"><span className="text-gold mt-0.5">•</span> The booking provider doesn&apos;t pay travel-advisor commissions</li>
                  <li className="flex gap-2"><span className="text-gold mt-0.5">•</span> You need standalone concierge work (dining, excursions) without a hotel booking</li>
                </ul>
                <p className="mt-5 text-xs text-soft/55">Fees are quoted upfront before any work begins.</p>
              </div>
            </div>
          )}
          {isTravel && (
            <div className="rounded-[2rem] overflow-hidden bg-gradient-to-br from-navy via-navy to-teal text-soft p-10 md:p-14 mb-16 relative" data-testid="fora-callout">
              <div className="blob bg-gold w-72 h-72 -bottom-20 -right-10 rounded-full opacity-25" />
              <div className="relative grid md:grid-cols-12 gap-10 items-center">
                <div className="md:col-span-7">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold mb-4">
                    <Globe2 className="w-3.5 h-3.5" /> Powered by Fora Travel
                  </div>
                  <h3 className="text-3xl md:text-4xl text-soft leading-tight">
                    Booked through Virtuoso, Four Seasons Preferred &amp; 4,500+ luxury partners.
                  </h3>
                  <p className="mt-4 text-soft/75 leading-relaxed max-w-2xl">
                    As a Fora Travel advisor, ConnectHer secures the perks the public cannot book online &mdash;
                    room upgrades, complimentary breakfast, late checkout, resort credits, and VIP recognition &mdash;
                    at the same rate offered on the hotel website.
                  </p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <a
                      href={BRAND.foraAdvisorUrl}
                      target="_blank" rel="noreferrer"
                      data-testid="fora-profile-link"
                      className="btn-primary"
                    >
                      <Plane className="w-4 h-4" /> Plan a Trip <ArrowUpRight className="w-4 h-4" />
                    </a>
                    <Link
                      to="/contact"
                      data-testid="fora-contact-link"
                      className="inline-flex items-center justify-center gap-2 bg-transparent border border-soft text-soft hover:bg-soft hover:text-navy rounded-full px-7 py-3 text-sm uppercase tracking-wider transition"
                    >
                      Request a Quote
                    </Link>
                  </div>
                </div>
                <div className="md:col-span-5">
                  <div className="bg-white/10 backdrop-blur border border-soft/15 rounded-2xl p-6">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">
                      Travel Insider List
                    </div>
                    <h4 className="text-xl text-soft mb-2">Group trips, retreats, &amp; flash deals.</h4>
                    <p className="text-sm text-soft/70 mb-5">
                      Be first to hear about ConnectHer group trips, women&apos;s retreats, and partner promotions.
                    </p>
                    <Newsletter variant="dark" source="travel" />
                  </div>
                </div>
              </div>
            </div>
          )}
          {isTravel && (
            <div
              data-testid="fora-referral-block"
              className="mb-16 rounded-[2rem] border border-gold/30 bg-gradient-to-br from-soft to-gold/5 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            >
              <div className="flex items-start gap-4 max-w-2xl">
                <div className="w-12 h-12 rounded-2xl bg-gold/15 text-gold flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1.5">
                    Become a Travel Advisor
                  </div>
                  <h3 className="text-2xl md:text-3xl text-navy leading-tight">
                    Love travel? Turn it into income with Fora.
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-navy/65 leading-relaxed">
                    Fora trains independent advisors like ConnectHer to book luxury travel and
                    earn commission. If you&apos;ve dreamed of building a travel business of your own,
                    join through our referral and we&apos;ll help you get started.
                  </p>
                </div>
              </div>
              <a
                href={BRAND.foraReferralUrl}
                target="_blank" rel="noreferrer"
                data-testid="fora-referral-cta"
                className="btn-primary flex-shrink-0"
              >
                Apply via ConnectHer <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          )}
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="eyebrow">Complete the ecosystem</div>
              <h2 className="mt-3 text-3xl md:text-4xl">You may also want&hellip;</h2>
            </div>
            <Link to="/services" className="btn-secondary">All Services</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-service-${s.slug}`} className="card overflow-hidden group block">
                <img src={s.image} alt={s.name} className="w-full h-40 object-cover group-hover:scale-105 transition duration-700" />
                <div className="p-5">
                  <h4 className="text-xl">{s.name}{s.comingSoon && <span className="ml-2 text-[10px] uppercase tracking-widest text-gold align-middle">Soon</span>}</h4>
                  <p className="mt-2 text-sm text-navy/60">{s.short}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

